// src/store/artists.js
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import artistService from '@/services/artistService';

export const useArtistStore = defineStore('artists', () => {
  // State
  const artists = ref([]);
  const currentArtist = ref(localStorage.getItem('defaultArtist') || null);
  const isLoading = ref(false);
  const error = ref(null);
  const spotifyResults = ref([]);

  // Getters
  const getArtists = computed(() => artists.value);
  const getCurrentArtist = computed(() => currentArtist.value);
  const getError = computed(() => error.value);
  const getSpotifyResults = computed(() => spotifyResults.value);
  const hasArtists = computed(() => artists.value.length > 0);

  const getDefaultArtist = () => {
    if (!artists.value.length) {
      return null;
    }

    let defaultArtist = artists.value[0];

    if (localStorage.getItem('defaultArtist')) {
      artists.value.forEach(artist => {
        if (artist._id === localStorage.getItem('defaultArtist')) {
          defaultArtist = artist;
        }
      });
    }

    return defaultArtist;
  };

  const getArtistById = (artistId) => {
    if (artistId && artists.value.length) {
      const match = artists.value.find(artist => artist._id === artistId);
      if (match) {
        return match;
      }
    }
    return getDefaultArtist();
  };

  // Shared in-flight request so concurrent callers (e.g. a view and the
  // ArtistPicker mounting together) trigger a single API call
  let fetchArtistsPromise = null;

  // Actions
  function fetchArtists() {
    if (fetchArtistsPromise) {
      return fetchArtistsPromise;
    }

    isLoading.value = true;
    error.value = null;

    fetchArtistsPromise = (async () => {
      try {
        const response = await artistService.getAll();
        artists.value = response;

        if (localStorage.getItem('defaultArtist')) {
          const defaultArtist = artists.value.find(artist => artist._id === localStorage.getItem('defaultArtist'));

          if (!defaultArtist) {
            localStorage.removeItem('defaultArtist');
            currentArtist.value = null;
          } else {
            localStorage.setItem('defaultArtist', defaultArtist._id);
            currentArtist.value = defaultArtist._id;
          }
        }

        return response;
      } catch (err) {
        console.error('Error fetching artists:', err);
        error.value = err.message || 'Failed to fetch artists';
        throw err;
      } finally {
        isLoading.value = false;
        fetchArtistsPromise = null;
      }
    })();

    return fetchArtistsPromise;
  }

  async function fetchArtist(id) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await artistService.get(id);
      currentArtist.value = response;
      return response;
    } catch (err) {
      console.error(`Error fetching artist ${id}:`, err);
      error.value = err.message || 'Failed to fetch artist details';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function saveArtist(artist) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await artistService.save(artist);
      const savedArtist = response.data || response;

      // Update local state
      if (artist._id) {
        // Update existing artist
        const index = artists.value.findIndex(a => a._id === artist._id);
        if (index !== -1) {
          artists.value[index] = savedArtist;
        }
      } else {
        // Add new artist to the list if it has an _id
        if (savedArtist && savedArtist._id) {
          artists.value.push(savedArtist);
        }
      }

      // Always update current artist
      currentArtist.value = savedArtist;

      return savedArtist;
    } catch (err) {
      console.error('Error saving artist:', err);
      error.value = err.message || 'Failed to save artist';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function checkArtistName(name, urlSearch = false) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await artistService.checkArtistName(name, urlSearch);
      const results = Array.isArray(response)
        ? response
        : response?.results;
      spotifyResults.value = Array.isArray(results) ? results : [];
      return response;
    } catch (err) {
      console.error('Error checking artist name:', err);
      error.value = err.message || 'Failed to check artist name';
      spotifyResults.value = [];
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  function resetCurrentArtist() {
    currentArtist.value = null;
  }

  function resetSpotifyResults() {
    spotifyResults.value = [];
  }

  return {
    // State
    artists,
    currentArtist,
    isLoading,
    error,
    spotifyResults,

    // Getters
    getArtists,
    getCurrentArtist,
    getError,
    getSpotifyResults,
    hasArtists,
    getDefaultArtist,
    getArtistById,

    // Actions
    fetchArtists,
    fetchArtist,
    saveArtist,
    checkArtistName,
    resetCurrentArtist,
    resetSpotifyResults,
  };
});